import { HttpClient } from '@angular/common/http';
import { ChangeDetectorRef, Component, Input, ViewChild, ViewContainerRef } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { SeConstraintApplierService, SeConstraintNotifierService } from '@se/se.platform.base';
import {
    IConfigControlButton,
    IConfigControlCustomButton,
    IConfigControlImage,
    IConfigControlLabel,
    IControlType,
    JsonObject,
} from '@se/cp.common';
import { Logger } from '@se/se.platform.base';
import { EstensaWaitingService, ModalWindowSize, ServerRootService } from '@se/platform';
import { DataBinding } from '@se/se.platform.base';
import { firstValueFrom } from 'rxjs';
import { finalize, take, takeUntil } from 'rxjs/operators';

import { rowProportional } from '../../cp-configuration/utils/utils';
import * as fromCp from '../../store/cp.state';
import { CpGenericControl, CpUserControl } from '../../cp-configuration/view-model';
import { CpDialogService } from '../../cp-dialog-service';
import { CpModelService } from '../../cp-model.service';
import { CpNodeService } from '../../cp-node.service';
import { CustomPageControlGridModalComponent } from '../../cp-ui-engine/custom-page-control-grid-modal/custom-page-control-grid-modal.component';
import { AbstractControlComponent } from '../abstract-control';
import { CpUserControlComponent } from '../user-control/user-control.component';
import { selectCpCustomPage } from '../../store/cp.selectors';
import { cpApplyNormalValues, cpOpenLinkedEcgs } from '../../store/cp.actions';

@Component({
    selector: 'cp-generic',
    templateUrl: './generic.component.html',
    styleUrls: ['./generic.component.scss'],
    standalone: false
})
export class CpGenericComponent extends AbstractControlComponent {
    @ViewChild('userControlContainer', { read: ViewContainerRef })
    userControlContainer!: ViewContainerRef;

    @Input() onlyIcon: boolean = false;

    control!: CpGenericControl;
    controlType = IControlType;

    isUserControlModalVisible = false;
    userControlModalSize = ModalWindowSize.medium;

    private isUserControlConstructed = false;

    constructor(
        private constraintApplierService: SeConstraintApplierService,
        private dialogService: CpDialogService,
        private http: HttpClient,
        private modelService: CpModelService,
        private nodeService: CpNodeService,
        private serverRootService: ServerRootService,
        private store: Store<fromCp.RootState>,
        private waitingService: EstensaWaitingService,
        cdr: ChangeDetectorRef,
        constraintNotifierService: SeConstraintNotifierService,
        logger: Logger,
    ) {
        super(cdr, constraintNotifierService, logger);
    }

    get cssClasses() {
        const config = this.control.config as IConfigControlLabel;
        if (!config) {
            return {};
        }

        return {
            'cp-bold': !!config.bold,
            'cp-italic': !!config.italic,
            'cp-underline': !!config.underlined,
        };
    }

    get imagePath() {
        const imageName = (this.control?.config as IConfigControlImage | undefined)?.id;
        if (!imageName) {
            return '';
        }

        return this.serverRootService.getCustomPagesSiteRoot() + 'images/' + imageName;
    }

    get labelUrl() {
        const config = this.control?.config as IConfigControlLabel | undefined;

        return config?.url ?? '';
    }

    get icon() {
        const config = this.control?.config as IConfigControlButton | undefined;

        return config?.icon;
    }

    get iconTooltip() {
        const config = this.control?.config as IConfigControlButton | undefined;

        return config?.iconTooltip ?? '';
    }

    get spacerCSSStyle() {
        return {
            height: `${this.control.getHeight()}px`,
            width: `${rowProportional(this.control.columnSpan)}px`,
        };
    }

    onButtonClick() {
        switch (this.control.controlType) {
            case IControlType.ConstraintButton:
                return this.onConstraintButtonClick();
            case IControlType.CustomButton:
                return this.onUserControlButtonClick();
            case IControlType.PanelButton:
                return this.onPanelButtonClick();
            case IControlType.ServerButton:
                return this.onServerButtonClick();
        }
    }

    protected onModelChange() {
        return false;
    }

    protected init() {
        // Nulla da fare.
    }

    private onUserControlButtonClick() {
        const config = this.control.config;
        if (!('idUserControl' in config)) {
            return;
        }

        switch (config.idUserControl) {
            case 'ApplyNormalValues':
                return this.store.dispatch(cpApplyNormalValues({}));
            case 'idUserControlLinkedEcgs':
                return this.store.dispatch(cpOpenLinkedEcgs());
            default:
                return this.openUserControlModal(config.idUserControl);
        }
    }

    private onConstraintButtonClick() {
        const config = this.control.config;
        if (!('idConstraints' in config)) {
            return;
        }

        if (config.idConstraints && config.idConstraints.length > 0) {
            this.constraintApplierService.applyMany(config.idConstraints);
        }
    }

    private onPanelButtonClick() {
        const config = this.control.config;
        if (!('idPanel' in config)) {
            return;
        }

        const grid = this.nodeService.getControlGridFromVirtualNode(config.idPanel, this.control.context);
        if (!grid) {
            throw new Error('Invalid panel configuration.');
        }

        if (config.idConstraints && config.idConstraints.length > 0) {
            this.constraintApplierService.applyMany(config.idConstraints);
        }
        this.dialogService.open(CustomPageControlGridModalComponent, { grid }, { zIndex: 9999 });
    }

    private async onServerButtonClick() {
        const config = this.control.config;
        if (!('endpoint' in config)) {
            return;
        }

        const idCustomPage = await firstValueFrom(this.store.pipe(select(selectCpCustomPage)));
        const model = this.modelService.getEntity(new DataBinding())?.toJSON();
        const endpoint = this.serverRootService.getServerRoot() + config.endpoint;

        this.waitingService.isWaiting = true;
        this.http
            .post<JsonObject>(endpoint, { idCustomPage, model: (model as any).Report })
            .pipe(finalize(() => (this.waitingService.isWaiting = false)))
            .subscribe((newModel) => this.modelService.notifyRootChange({ Report: newModel }));
    }

    private openUserControlModal(userControlId: string) {
        if (!this.isUserControlConstructed) {
            const component = this.userControlContainer.createComponent(CpUserControlComponent);
            component.instance.control = new CpUserControl(
                {
                    config: [
                        {
                            id: userControlId,
                            parameters: (this.control.config as unknown as IConfigControlCustomButton).parameters,
                        },
                    ],
                } as any,
                undefined,
            );
            component.instance.form = this.form;
            component.instance.isDisabledByBinding = this.isDisabledByBinding;
            component.instance.edited?.pipe(takeUntil(this.destroyed$), take(1)).subscribe(() => this.edited.emit());
            component.instance.cardId = this.cardId;
            component.instance.changeDetectorRef.detectChanges();

            this.userControlModalSize = component.instance.getSize();
            this.isUserControlConstructed = true;
        }

        this.isUserControlModalVisible = true;
    }
}
